import { convexQuery } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { FunctionReturnType } from 'convex/server'
import { IconOpenInNewWindowOutline18, IconStackOutline18 } from 'nucleo-ui-outline-18'
import { CatalogThumb } from '~/components/admin/catalog-bits'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { BackLink, PageHeader } from '~/components/ui/page-header'
import { Spinner } from '~/components/ui/spinner'
import { useGrowingPage } from '~/hooks/use-growing-page'
import { formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/admin/catalog/collections')({
  component: AdminCatalogCollections,
  head: () => ({ meta: [{ title: titleFor(msg`Collections`) }] }),
})

const PAGE_SIZE = 40

type CollectionRow = FunctionReturnType<
  typeof api.catalogAdminQueries.listCollections
>['page'][number]

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

/** One collection: up to four product thumbnails, the name and brand, when it appeared, how many products. */
const CollectionCard = ({ collection }: { collection: CollectionRow }) => {
  const { t, i18n } = useLingui()
  return (
    // The source link is a SIBLING of the card link, never a child: `Link`
    // renders an `<a>`, and an anchor inside an anchor is invalid HTML that
    // the parser restructures, leaving assistive technology with a malformed
    // link. Stopping propagation would only have hidden the symptom.
    <li className="flex h-full flex-col gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent/40">
      <Link
        className="flex flex-col gap-3"
        search={{ collection: collection._id }}
        to="/admin/catalog"
      >
        <div className="grid grid-cols-4 gap-1">
          {Array.from({ length: 4 }, (_, index) => (
            <CatalogThumb
              alt=""
              className="aspect-square size-auto w-full"
              key={collection.thumbUrls[index] ?? `empty-${index}`}
              url={collection.thumbUrls[index] ?? null}
            />
          ))}
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-medium text-sm">
            {collection.name}
            {collection.year === null ? '' : ` · ${collection.year}`}
          </span>
          <span className="truncate text-muted-foreground text-xs">{collection.brandName}</span>
        </div>
      </Link>
      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-xs">
        <span title={t`First seen`}>{formatRelativeTime(collection.firstSeenAt, i18n)}</span>
        <span>·</span>
        <span className="tabular-nums">{t`${i18n.number(collection.productCount)} products`}</span>
        {collection.sourceUrl === null ? null : (
          <a
            className="ml-auto inline-flex items-center gap-1 text-primary hover:underline"
            href={collection.sourceUrl}
            rel="noreferrer"
            target="_blank"
          >
            {hostOf(collection.sourceUrl)}
            <IconOpenInNewWindowOutline18 className="size-3" />
          </a>
        )}
      </div>
    </li>
  )
}

/**
 * `/admin/catalog/collections` — brand collections newest-first, derived
 * from the `collectionRef` pages carry. The admin-side reading of a launch
 * feed: no editorial, no staff flag, just what the pages said and when.
 */
function AdminCatalogCollections() {
  const { t } = useLingui()
  const { paginationOpts, loadMore } = useGrowingPage(PAGE_SIZE)
  const { data } = useQuery(
    convexQuery(api.catalogAdminQueries.listCollections, { paginationOpts })
  )

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        above={<BackLink to="/admin/catalog">{t`Product catalog`}</BackLink>}
        title={t`Collections`}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
        {data === undefined ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : data.page.length === 0 ? (
          <EmptyState
            description={t`Collections appear when a page names one through collectionRef; nothing has yet.`}
            icon={IconStackOutline18}
            title={t`No collections yet`}
          />
        ) : (
          <>
            <ul className="grid grid-cols-1 gap-3 py-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {data.page.map((collection) => (
                <CollectionCard collection={collection} key={collection._id} />
              ))}
            </ul>
            {data.isDone ? null : (
              <div className="flex justify-center py-3">
                <Button onClick={loadMore} size="sm" type="button" variant="outline">
                  <Trans>Load more</Trans>
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
